import React from "react";
import {
	Box,
	InputAdornment,
	MenuItem,
	Select,
	TextField,
	Button,
	FormControl,
	Checkbox,
	ListItemText,
	Autocomplete,
	SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CircularProgress from "@mui/material/CircularProgress";
import { PastoralCategory, PastoralFilters, PREDEFINED_TAG_NAMES } from "../../hooks/usePastoralNotes";
import { useStaffSearch, StaffSearchResult } from "../../hooks/useStaffSearch";
import { useEffect, useState } from "react";

interface FilterBarProps {
	filters: PastoralFilters;
	onChange: (filters: PastoralFilters) => void;
	categories?: PastoralCategory[];
	showSearch?: boolean;
	showDownload?: boolean;
	onDownload?: () => void;
	isExporting?: boolean;
	drawerOpen?: boolean;
	/** When provided, shows an "Add Note" button that invokes this. */
	onAddNote?: () => void;
}

const YEARS = ["Kindergarten", "Year 1", "Year 2", "Year 3", "Year 4", "Year 5", "Year 6", "Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12"];
const DATE_PRESETS: { value: string; label: string; days: number }[] = [
	{ value: "last_7_days", label: "Last 7 days", days: 7 },
	{ value: "last_30_days", label: "Last 30 days", days: 30 },
	{ value: "last_90_days", label: "Last 90 days", days: 90 },
	{ value: "all_time", label: "All time", days: 0 },
];


function isoSubDays(n: number): string {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return d.toISOString().split("T")[0];
}

const HIDDEN_CHIP_KEYS = new Set(["search", "from_date", "to_date", "page", "limit", "sort"]);

const CHIP_LABELS: Record<string, string> = {
	last_7_days: "Last 7 days",
	last_30_days: "Last 30 days",
	last_90_days: "Last 90 days",
	all_time: "All time",
	this_term: "This term",
	Created: "Active",
	Unresolved: "Monitoring",
	Resolved: "Resolved",
	Actioned: "Actioned",
	Viewed: "Viewed",
};

const FilterBar: React.FC<FilterBarProps> = ({
	filters,
	onChange,
	categories = [],
	showSearch = true,
	showDownload = false,
	onDownload,
	isExporting = false,
	drawerOpen = false,
	onAddNote,
}) => {
	const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c.label]));
	const handleSelect = (field: keyof PastoralFilters) => (e: SelectChangeEvent) => {
		onChange({ ...filters, [field]: e.target.value || undefined });
	};

	// Author filter — a staff type-ahead backed by the federated /search endpoint.
	const { results: staffResults, isSearching: isSearchingStaff, search: searchStaff, clear: clearStaff } = useStaffSearch();
	const [authorValue, setAuthorValue] = useState<StaffSearchResult | null>(null);
	// Keep the picker in sync when the filter is cleared elsewhere (e.g. Clear all).
	useEffect(() => {
		if (!filters.author) setAuthorValue(null);
	}, [filters.author]);

	const handleDateSelect = (e: SelectChangeEvent) => {
		const preset = e.target.value;
		const match = DATE_PRESETS.find((p) => p.value === preset);
		onChange({
			...filters,
			date_from: preset || undefined,
			// "All time" (days: 0) clears the lower bound entirely.
			from_date: match && match.days > 0 ? isoSubDays(match.days) : undefined,
		});
	};

	const handleTagsChange = (e: SelectChangeEvent<string[]>) => {
		const value = e.target.value;
		const selected = typeof value === "string" ? value.split(",") : value;
		// Blank the filter entirely when nothing is selected so it doesn't count
		// as an active filter (and isn't sent as an empty param).
		onChange({ ...filters, tags: selected.length ? selected : undefined });
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange({ ...filters, search: e.target.value || undefined });
	};

	const activeFilters = Object.entries(filters).filter(
		([k, v]) => v !== undefined && v !== "" && !HIDDEN_CHIP_KEYS.has(k)
	);

	const clearAll = () => onChange({});

	const selectSx = {
		minWidth: 130,
		height: 36,
		bgcolor: "#fff",
		"& .MuiSelect-select": { py: "6px" },
	};

	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
				{showSearch && (
					<TextField
						placeholder="Search student, tags, keywords..."
						value={filters.search ?? ""}
						onChange={handleSearch}
						size="small"
						sx={{ width: 280, bgcolor: "#fff", borderRadius: 1 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
							endAdornment: filters.search ? (
								<InputAdornment position="end">
									<IconButton
										size="small"
										onClick={() => onChange({ ...filters, search: undefined })}
										edge="end"
										aria-label="Clear search"
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
						}}
					/>
				)}

				<FormControl size="small">
					<Select
						displayEmpty
						value={filters.category ?? ""}
						onChange={handleSelect("category")}
						sx={selectSx}
						renderValue={(v) => (v ? (categoryMap[v] ?? v) : "Category")}
					>
						<MenuItem value="">All Categories</MenuItem>
						{categories.map((c) => (
							<MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small">
					<Select
						displayEmpty
						value={filters.year ?? ""}
						onChange={handleSelect("year")}
						sx={selectSx}
						renderValue={(v) => v || "Year"}
					>
						<MenuItem value="">All Years</MenuItem>
						{YEARS.map((y) => (
							<MenuItem key={y} value={y}>{y}</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small">
					<Select
						multiple
						displayEmpty
						value={filters.tags ?? []}
						onChange={handleTagsChange}
						sx={selectSx}
						renderValue={(selected) => {
							const arr = selected as string[];
							if (arr.length === 0) return "Tags";
							return arr.length === 1 ? arr[0] : `${arr.length} tags`;
						}}
					>
						{PREDEFINED_TAG_NAMES.map((t) => (
							<MenuItem key={t} value={t}>
								<Checkbox size="small" checked={(filters.tags ?? []).includes(t)} />
								<ListItemText primary={t} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Autocomplete<StaffSearchResult>
					size="small"
					sx={{ minWidth: 200, bgcolor: "#fff" }}
					options={staffResults}
					loading={isSearchingStaff}
					value={authorValue}
					getOptionLabel={(o) => o.title}
					isOptionEqualToValue={(o, v) => o.id === v.id}
					// The server already filters by the query; don't re-filter client-side.
					filterOptions={(x) => x}
					noOptionsText="Type to search staff"
					renderOption={(props, option) => (
						<li {...props} key={option.id}>
							<Box>
								<Box component="span" sx={{ fontSize: 14 }}>{option.title}</Box>
								{option.subtitle && (
									<Box component="span" sx={{ fontSize: 12, color: "text.secondary", display: "block" }}>
										{option.subtitle}
									</Box>
								)}
							</Box>
						</li>
					)}
					onInputChange={(_e, value, reason) => {
						if (reason === "input") searchStaff(value);
					}}
					onChange={(_e, value) => {
						setAuthorValue(value);
						// Send the staff member's ContactPersonal _id; the /feed `author`
						// param matches it against the note author for an exact match.
						onChange({ ...filters, author: value?.id || undefined });
						if (!value) clearStaff();
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="Author"
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{isSearchingStaff ? <CircularProgress size={14} thickness={5} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
				<FormControl size="small">
					<Select
						displayEmpty
						value={filters.date_from ?? ""}
						onChange={handleDateSelect}
						sx={selectSx}
						renderValue={(v) => (v ? (CHIP_LABELS[v] ?? String(v)) : "Date Range")}
					>
						<MenuItem value="">All Dates</MenuItem>
						{DATE_PRESETS.map((p) => (
							<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
						))}
					</Select>
				</FormControl>

				{activeFilters.length > 0 && (
					<Button
						size="small"
						onClick={clearAll}
						sx={{ textTransform: "none", fontSize: 13, color: "text.secondary", ml: 0.5 }}
					>
						Clear all
					</Button>
				)}

				{showDownload && (
					<Button
						variant="outlined"
						size="small"
						disabled={isExporting}
						startIcon={isExporting ? <CircularProgress size={14} thickness={5} /> : <FileDownloadIcon />}
						onClick={onDownload}
						sx={{ ml: "auto", mr: drawerOpen ? 2 : 0, transition: "margin-right 0.3s ease", textTransform: "none", height: 36, borderColor: "#dde3f0", color: "#333" }}
					>
						{isExporting ? "Exporting..." : "Export"}
					</Button>
				)}

				{onAddNote && (
					<Button
						variant="contained"
						size="small"
						startIcon={<AddIcon sx={{ fontSize: 16 }} />}
						onClick={onAddNote}
						sx={{ ml: showDownload ? 1.5 : "auto", bgcolor: "#002f67", "&:hover": { bgcolor: "#001a4a" }, textTransform: "none", height: 36, whiteSpace: "nowrap" }}
					>
						Add Note
					</Button>
				)}
			</Box>



		</Box>
	);
};

export default FilterBar;
